import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * Info cards that open on hover with a mouse, and on tap with touch or pen.
 *
 * Before: the cards only listened for mouseenter/mouseleave, which tablets
 * don't reliably fire. Now:
 *   - mouse: hover opens, leaving closes (same as before)
 *   - touch/pen: tap opens, tapping it again, the dimmed background, or
 *     anywhere outside the section closes
 *   - keyboard: Tab to a label, Enter/Space toggles, Escape closes
 *
 * Usage:
 *   const { active, close, containerRef, bind } = useInfoPopover();
 *   <div ref={containerRef}> ... <span {...bind('status-head')}>Status</span>
 */
const useInfoPopover = () => {
  const [active, setActive] = useState(null);
  const lastPointerType = useRef('mouse');
  const containerRef = useRef(null);

  const close = useCallback(() => setActive(null), []);

  // Close when tapping/clicking outside this section, or on Escape.
  useEffect(() => {
    if (active == null) return undefined;
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setActive(null);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setActive(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [active]);

  const bind = (id) => ({
    id,
    role: 'button',
    tabIndex: 0,
    'aria-expanded': active === id,
    // Above the dimmed overlay while its card is open (was set via element.style before)
    style: active === id ? { zIndex: 7 } : undefined,
    onPointerDown: (e) => {
      lastPointerType.current = e.pointerType || 'mouse';
    },
    onPointerEnter: (e) => {
      if (e.pointerType === 'mouse') setActive(id);
    },
    onPointerLeave: (e) => {
      if (e.pointerType === 'mouse') setActive((cur) => (cur === id ? null : cur));
    },
    onClick: () => {
      // Mouse users already opened it by hovering; a click shouldn't close it.
      if (lastPointerType.current === 'mouse') {
        setActive(id);
        return;
      }
      setActive((cur) => (cur === id ? null : id));
    },
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        lastPointerType.current = 'keyboard';
        setActive((cur) => (cur === id ? null : id));
      }
    },
  });

  return { active, close, containerRef, bind };
};

export default useInfoPopover;
